<script lang="ts">
  import { onMount, onDestroy, createEventDispatcher } from "svelte";
  import EmblaCarousel from "embla-carousel";
  import type {
    EmblaCarouselType,
    EmblaOptionsType,
    EmblaPluginType,
  } from "embla-carousel";
  import AutoScroll from "embla-carousel-auto-scroll";
  import ClassNames from "embla-carousel-class-names";

  export let options: EmblaOptionsType = {};
  export let plugins: EmblaPluginType[] = [];
  export let autoplay: boolean = false;
  export let autoplayOptions: {
    delay?: number;
    stopOnInteraction?: boolean;
    stopOnMouseEnter?: boolean;
    stopOnFocusIn?: boolean;
  } = {};
  export let navigation: boolean = false;
  export let pagination: boolean = false;
  export let className: string = "";
  export let loop: boolean = true;
  export let slidesToScroll: number = 1;
  export let breakpoints: Record<number, Partial<EmblaOptionsType>> = {};
  export let duration: number = 10;
  export let startIndex: number = 0;
  export let align: "start" | "center" | "end" = "start";
  export let containScroll: "trimSnaps" | "keepSnaps" | false = "trimSnaps";
  export let dragFree: boolean = false;
  export let direction: "ltr" | "rtl" = "ltr";
  export let pauseOnHover: boolean = true;

  let emblaApi: EmblaCarouselType | undefined;
  let emblaNode: HTMLElement;
  let selectedIndex: number = 0;
  let scrollSnaps: number[] = [];
  let canScrollPrev: boolean = false;
  let canScrollNext: boolean = false;

  let isMobile: boolean = false;

  function getCurrentOptions(): EmblaOptionsType {
    // Mobile-optimized defaults
    const mobileDefaults = isMobile
      ? {
          dragThreshold: 15, // Higher threshold for mobile
          skipSnaps: false, // Ensure snapping on mobile
          inViewThreshold: 0.7, // More strict view threshold
          speed: 10, // Slightly slower for smoother mobile experience
        }
      : {};

    const baseOptions: EmblaOptionsType = {
      loop,
      slidesToScroll,
      startIndex,
      align,
      containScroll,
      dragFree,
      direction,
      duration,
      // Touch gesture improvements
      dragThreshold: 10,
      preventScrollOnTouch: false, // Don't prevent vertical scrolling
      watchDrag: true,
      watchResize: true,
      watchSlides: true,
      ...mobileDefaults,
      ...options,
    };

    if (typeof window === "undefined") return baseOptions;

    const width = window.innerWidth;
    const breakpointKeys = Object.keys(breakpoints)
      .map(Number)
      .sort((a, b) => a - b);

    for (const breakpoint of breakpointKeys) {
      if (width >= breakpoint) {
        Object.assign(baseOptions, breakpoints[breakpoint]);
      }
    }

    return baseOptions;
  }

  function getPlugins(): EmblaPluginType[] {
    const allPlugins = [...plugins];

    if (autoplay) {
      allPlugins.push(
        AutoScroll({
          playOnInit: true,
          stopOnInteraction: false,
          stopOnMouseEnter: pauseOnHover,
          stopOnFocusIn: true,
          ...autoplayOptions,
        }),
      );
    }

    allPlugins.push(
      ClassNames({
        selected: "is-selected",
        inView: "is-in-view",
        draggable: "is-draggable",
        dragging: "is-dragging",
      }),
    );

    return allPlugins;
  }

  function onSelect(api: EmblaCarouselType): void {
    if (!api) return;
    const previousIndex = selectedIndex;
    selectedIndex = api.selectedScrollSnap();
    canScrollPrev = api.canScrollPrev();
    canScrollNext = api.canScrollNext();

    dispatch("activeIndexChange", {
      activeIndex: selectedIndex,
      previousIndex,
    });
  }

  function scrollPrev(): void {
    emblaApi?.scrollPrev();
  }

  function scrollNext(): void {
    emblaApi?.scrollNext();
  }

  function scrollTo(index: number): void {
    emblaApi?.scrollTo(index);
  }

  const dispatch = createEventDispatcher();

  onMount(() => {
    if (!emblaNode) return;

    // Detect mobile
    isMobile =
      typeof window !== "undefined" &&
      (window.innerWidth < 768 || "ontouchstart" in window);

    const currentOptions = getCurrentOptions();
    const currentPlugins = getPlugins();

    emblaApi = EmblaCarousel(emblaNode, currentOptions, currentPlugins);

    if (emblaApi) {
      scrollSnaps = emblaApi.scrollSnapList();
      emblaApi.on("select", () => onSelect(emblaApi!));
      emblaApi.on("reInit", () => onSelect(emblaApi!));

      onSelect(emblaApi);
    }

    const handleResize = () => {
      if (emblaApi) {
        const newOptions = getCurrentOptions();
        emblaApi.reInit(newOptions, getPlugins());
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  });

  onDestroy(() => {
    emblaApi?.destroy();
  });

  export function getApi(): EmblaCarouselType | undefined {
    return emblaApi;
  }

  export function play(): void {
    const autoplayPlugin = emblaApi?.plugins()?.autoplay;
    if (autoplayPlugin && "play" in autoplayPlugin) {
      (autoplayPlugin as any).play();
    }
  }

  export function stop(): void {
    const autoplayPlugin = emblaApi?.plugins()?.autoplay;
    if (autoplayPlugin && "stop" in autoplayPlugin) {
      (autoplayPlugin as any).stop();
    }
  }
</script>

<style lang="scss">
  .embla {
    position: relative;
    width: 100%;
    overflow: hidden; /* Prevent content overflow immediately */

    &__viewport {
      overflow: hidden;
      width: 100%;
      height: 100%;
      position: relative;

      &.is-draggable {
        cursor: move;
        cursor: grab;
      }

      &.is-dragging {
        cursor: grabbing;
      }
    }

    &__container {
      display: flex;
      touch-action: pan-y pinch-zoom; /* Allow vertical scrolling, horizontal handled by Embla */
      backface-visibility: hidden;
      -webkit-backface-visibility: hidden; /* Safari */

      /* Only use hardware acceleration when actually scrolling */
      @media (max-width: 768px) {
        will-change: auto; /* Let browser decide when to optimize */
        transform: translate3d(
          0,
          0,
          0
        ); /* Use 3D transform for smoother scrolling */
      }

      @media (min-width: 769px) {
        will-change: transform;
        transform: translateZ(0);
      }

      /* Disable text selection while dragging */
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      -khtml-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;

      /* Mobile-optimized scrolling */
      @media (max-width: 768px) {
        overscroll-behavior-x: contain; /* Prevent horizontal overscroll */
        overscroll-behavior-y: auto; /* Allow normal vertical scrolling */
        scroll-snap-type: x mandatory;
        -webkit-overflow-scrolling: touch;
      }
    }

    &__slide {
      flex: 0 0 100%;
      min-width: 0;
      position: relative;
      height: 100%;
      /* Remove will-change from slides to reduce GPU memory */
      will-change: auto;

      /* Prevent GPU layer creation for non-visible slides on mobile */
      @media (max-width: 768px) {
        transform: none; /* No transform by default */
        opacity: 1;

        &:not(.is-selected) {
          /* Slides not in view don't need GPU layers */
          backface-visibility: hidden;
        }
      }

      /* Safari 18+ optimization */
      @supports (content-visibility: auto) {
        content-visibility: auto;
        contain-intrinsic-size: 0 500px; /* Adjust based on your slide heights */
      }

      /* Animation states from Class Names plugin */
      &.is-snapped {
        opacity: 1;
      }

      &.is-in-view {
        opacity: 1;
      }
    }

    &__navigation {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      justify-content: space-between;
      width: 100%;
      padding: 0 1rem;
      pointer-events: none;
      z-index: 1;
    }

    &__button {
      appearance: none;
      background: rgba(0, 0, 0, 0.5);
      border: none;
      border-radius: 50%;
      color: white;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 3rem;
      height: 3rem;
      pointer-events: all;
      transition:
        opacity 0.2s,
        background-color 0.2s;

      &:hover:not(:disabled) {
        background: rgba(0, 0, 0, 0.7);
      }

      &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }

      svg {
        width: 1.5rem;
        height: 1.5rem;
      }
    }

    &__button--prev {
      left: 1rem;
    }

    &__button--next {
      right: 1rem;
      margin-left: auto;
    }

    &__dots {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 0.5rem;
      padding: 1rem 0;
    }

    &__dot {
      appearance: none;
      background: rgba(0, 0, 0, 0.2);
      border: none;
      border-radius: 50%;
      cursor: pointer;
      width: 0.5rem;
      height: 0.5rem;
      padding: 0;
      transition:
        background-color 0.2s,
        transform 0.2s;

      &:hover {
        background: rgba(0, 0, 0, 0.4);
      }

      &--selected {
        background: rgba(0, 0, 0, 0.8);
        transform: scale(1.2);
      }
    }
  }
</style>

<div class="embla {className}">
  <div class="embla__viewport" bind:this={emblaNode}>
    <div class="embla__container">
      <slot />
    </div>
  </div>

  {#if navigation}
    <div class="embla__navigation">
      <button
        class="embla__button embla__button--prev"
        on:click={scrollPrev}
        disabled={!canScrollPrev}
        aria-label="Previous slide"
        type="button"
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
        </svg>
      </button>
      <button
        class="embla__button embla__button--next"
        on:click={scrollNext}
        disabled={!canScrollNext}
        aria-label="Next slide"
        type="button"
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" />
        </svg>
      </button>
    </div>
  {/if}

  {#if pagination && scrollSnaps.length > 0}
    <div class="embla__dots">
      {#each scrollSnaps as _, index}
        <button
          class="embla__dot {selectedIndex === index
            ? 'embla__dot--selected'
            : ''}"
          on:click={() => scrollTo(index)}
          aria-label="Go to slide {index + 1}"
          type="button"
        />
      {/each}
    </div>
  {/if}
</div>
